import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { toast } from 'react-hot-toast';
import {
  Plus,
  Bell,
  Clock,
  Check,
  AlertTriangle,
  AlarmClock,
  CalendarClock,
  Flag
} from 'lucide-react';
import Modal from '../Modal';
import CreateReminderModal from '../dashboard/CreateReminderModal';
import PostponeReminderModal from '../dashboard/PostponeReminderModal';
import {
  reminderApi,
  Reminder,
  EntityType,
  getReminderPriorityColor
} from '../../services/reminderService';
import { LeadDto, formatDate } from '../../services/leadsService';

interface LeadRemindersModalProps {
  isOpen: boolean;
  onClose: () => void;
  lead: LeadDto;
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const Toolbar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
`;

const LeadInfo = styled.div`
  font-size: 13px;
  color: var(--text-secondary);

  strong {
    color: var(--text-primary);
    font-weight: 600;
  }
`;

const AddButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  border: 1px solid var(--primary-color);
  background: var(--primary-color);
  color: white;
  border-radius: var(--border-radius-md);
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: transparent;
    color: var(--primary-color);
  }
`;

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 600;
  color: var(--text-primary);
`;

const SectionIcon = styled.span<{ $color: string }>`
  display: inline-flex;
  align-items: center;
  color: ${props => props.$color};
`;

const SectionCount = styled.span`
  font-size: 13px;
  color: var(--text-secondary);
  font-weight: 400;
`;

const ItemsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const ItemRow = styled.div<{ $accent: string }>`
  border: 1px solid var(--border-color);
  border-right: 3px solid ${props => props.$accent};
  border-radius: var(--border-radius-md);
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  background: var(--surface-color);
`;

const ItemHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
`;

const ItemTitle = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: var(--text-primary);
`;

const ItemDateTime = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  color: var(--text-secondary);
  white-space: nowrap;
`;

const ItemDescription = styled.div`
  font-size: 13px;
  color: var(--text-secondary);
  line-height: 1.5;
`;

const ItemFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
`;

const StatusRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
`;

const StatusIndicator = styled.span<{ $color?: string }>`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  color: ${props => props.$color || 'var(--text-secondary)'};
`;

const ItemActions = styled.div`
  display: flex;
  gap: 8px;
`;

const ActionButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 6px 12px;
  border: 1px solid var(--border-color);
  background: var(--surface-color);
  color: var(--text-primary);
  border-radius: var(--border-radius-sm);
  font-size: 13px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: var(--hover-background);
    border-color: var(--primary-color);
    color: var(--primary-color);
  }
`;

const EmptyState = styled.div`
  padding: 16px;
  text-align: center;
  font-size: 13px;
  color: var(--text-secondary);
  border: 1px dashed var(--border-color);
  border-radius: var(--border-radius-md);
`;

const LoadingState = styled.div`
  padding: 24px;
  text-align: center;
  color: var(--text-secondary);
`;

const LeadRemindersModal: React.FC<LeadRemindersModalProps> = ({ isOpen, onClose, lead }) => {
  const [loading, setLoading] = useState(true);
  const [urgentReminders, setUrgentReminders] = useState<Reminder[]>([]);
  const [futureReminders, setFutureReminders] = useState<Reminder[]>([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [postponeReminder, setPostponeReminder] = useState<Reminder | null>(null);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const reminders = await reminderApi.getLeadReminders(lead.id);

      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const tomorrow = new Date(today);
      tomorrow.setDate(tomorrow.getDate() + 1);

      const urgent = reminders
        .filter(r => r.status === 1 && new Date(r.dueDateTime) < tomorrow)
        .sort((a, b) => a.priority - b.priority);

      const future = reminders
        .filter(r => r.status === 1 && new Date(r.dueDateTime) >= tomorrow)
        .sort((a, b) => new Date(a.dueDateTime).getTime() - new Date(b.dueDateTime).getTime());

      setUrgentReminders(urgent);
      setFutureReminders(future);
    } catch (error) {
      console.error('Error loading lead reminders:', error);
      toast.error('שגיאה בטעינת תזכורות הליד');
    } finally {
      setLoading(false);
    }
  }, [lead.id]);

  useEffect(() => {
    if (isOpen) {
      loadData();
    }
  }, [isOpen, loadData]);

  const handleComplete = async (reminderId: number) => {
    try {
      await reminderApi.handleReminder(reminderId, { handledNotes: 'הושלם' });
      toast.success('התזכורת סומנה כבוצעה');
      loadData();
    } catch (error) {
      console.error('Error completing reminder:', error);
      toast.error('שגיאה בסימון התזכורת כבוצעה');
    }
  };

  const handleCreateSaved = () => {
    setShowCreateModal(false);
    loadData();
  };

  const handlePostpone = async (newDate: string, reason?: string) => {
    if (!postponeReminder) return;
    try {
      await reminderApi.postponeReminder(postponeReminder.id, { newDueDate: newDate, reason });
      toast.success('התזכורת נדחתה');
      setPostponeReminder(null);
      loadData();
    } catch (error) {
      console.error('Error postponing reminder:', error);
      toast.error('שגיאה בדחיית התזכורת');
    }
  };

  const renderReminder = (reminder: Reminder) => {
    const accent = getReminderPriorityColor(reminder.priority);
    return (
      <ItemRow key={reminder.id} $accent={accent}>
        <ItemHeader>
          <ItemTitle>{reminder.title}</ItemTitle>
          <ItemDateTime>
            <Clock size={14} strokeWidth={2} />
            {formatDate(reminder.dueDateTime)}
          </ItemDateTime>
        </ItemHeader>

        {reminder.description && (
          <ItemDescription>{reminder.description}</ItemDescription>
        )}

        <ItemFooter>
          <StatusRow>
            <StatusIndicator $color={accent}>
              <Flag size={14} strokeWidth={2} />
              {reminder.priorityName}
            </StatusIndicator>
            <StatusIndicator>
              <Bell size={14} strokeWidth={2} />
              {reminder.reminderTypeName}
            </StatusIndicator>
            {reminder.isOverdue && (
              <StatusIndicator $color="var(--error-color)">
                <AlarmClock size={14} strokeWidth={2} />
                פג תוקף
              </StatusIndicator>
            )}
          </StatusRow>

          <ItemActions>
            <ActionButton onClick={() => setPostponeReminder(reminder)}>
              <CalendarClock size={15} strokeWidth={2} />
              דחה
            </ActionButton>
            <ActionButton onClick={() => handleComplete(reminder.id)}>
              <Check size={15} strokeWidth={2} />
              סמן כבוצע
            </ActionButton>
          </ItemActions>
        </ItemFooter>
      </ItemRow>
    );
  };

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} title="תזכורות הליד" width="640px">
        <Container>
          <Toolbar>
            <LeadInfo>
              ליד: <strong>{lead.fullName}</strong>
            </LeadInfo>
            <AddButton onClick={() => setShowCreateModal(true)}>
              <Plus size={16} strokeWidth={2} />
              הוסף תזכורת
            </AddButton>
          </Toolbar>

          {loading ? (
            <LoadingState>טוען תזכורות...</LoadingState>
          ) : (
            <>
              <Section>
                <SectionHeader>
                  <SectionIcon $color="var(--error-color)">
                    <AlertTriangle size={18} strokeWidth={2} />
                  </SectionIcon>
                  תזכורות דחופות
                  {urgentReminders.length > 0 && (
                    <SectionCount>({urgentReminders.length})</SectionCount>
                  )}
                </SectionHeader>
                <ItemsList>
                  {urgentReminders.length === 0 ? (
                    <EmptyState>אין תזכורות דחופות</EmptyState>
                  ) : (
                    urgentReminders.map(renderReminder)
                  )}
                </ItemsList>
              </Section>

              <Section>
                <SectionHeader>
                  <SectionIcon $color="var(--primary-color)">
                    <CalendarClock size={18} strokeWidth={2} />
                  </SectionIcon>
                  תזכורות עתידיות
                  {futureReminders.length > 0 && (
                    <SectionCount>({futureReminders.length})</SectionCount>
                  )}
                </SectionHeader>
                <ItemsList>
                  {futureReminders.length === 0 ? (
                    <EmptyState>אין תזכורות עתידיות</EmptyState>
                  ) : (
                    futureReminders.map(renderReminder)
                  )}
                </ItemsList>
              </Section>
            </>
          )}
        </Container>
      </Modal>

      {showCreateModal && (
        <CreateReminderModal
          isOpen={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          onSave={handleCreateSaved}
          prefilledEntityType={EntityType.Lead}
          prefilledEntityId={lead.id}
        />
      )}

      {postponeReminder && (
        <PostponeReminderModal
          isOpen={!!postponeReminder}
          reminder={postponeReminder}
          onClose={() => setPostponeReminder(null)}
          onPostpone={handlePostpone}
        />
      )}
    </>
  );
};

export default LeadRemindersModal;
